import React from 'react';
import { useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Shield, Database, Users, Cookie, Mail, Landmark, Info, UserCheck } from 'lucide-react';
import { SchemaOrg } from '@/components/utils/SchemaOrg';

const PrivacyPolicyPage = () => {
  const location = useLocation();
  const pageTitle = "Privacy Policy | WebFiny";
  const pageDescription = "Understand how WebFiny collects, uses, and protects your personal data and information when you use our PDF APIs, platform, and services.";
  const canonicalUrl = `${window.location.origin}${location.pathname}`;
  const lastUpdated = "June 15, 2025";
  const companyName = "FinyCore Inc.";
  const companyAddress = "123 Innovation Drive, Tech Park, Suite 404, Future City, FC 98765, World";
  const dpoEmail = "privacy@webfiny.com";

  const sections = [
    {
      id: "introduction",
      icon: <Info className="w-6 h-6 mr-3 text-primary" />,
      title: "1. Introduction",
      content: `This Privacy Policy describes how ${companyName} ("WebFiny", "we", "us", or "our") collects, uses, shares, and protects your personal information when you visit our website (webfiny.com), use our Application Programming Interfaces (APIs), or otherwise interact with our services (collectively, the "Services"). We are committed to protecting your privacy and handling your data in an open and transparent manner.`
    },
    {
      id: "information-collected",
      icon: <Database className="w-6 h-6 mr-3 text-primary" />,
      title: "2. Information We Collect",
      subsections: [
        {
          title: "a. Information You Provide to Us:",
          content: `When you register for an account, use our Services, or communicate with us, you may provide us with personal information such as your name, email address, company name, payment information, and any content you upload or process through our APIs (e.g., PDF files).`
        },
        {
          title: "b. Information We Collect Automatically:",
          content: `When you use our Services, we may automatically collect certain information, including your IP address, browser type, operating system, device information, usage data (e.g., API call logs, features used, pages visited), and information collected through cookies and similar tracking technologies. For files processed via our APIs, we temporarily store them only for the duration necessary to perform the requested operation; these files are typically deleted shortly after processing.`
        },
        {
          title: "c. Information from Third Parties:",
          content: `We may receive information about you from third-party sources, such as payment processors or identity verification services, to help us provide and improve our Services.`
        }
      ]
    },
    {
      id: "how-we-use-information",
      icon: <Shield className="w-6 h-6 mr-3 text-primary" />,
      title: "3. How We Use Your Information",
      content: `We use the information we collect for various purposes, including:
        <ul class="list-disc list-inside space-y-1 mt-2">
          <li>To provide, operate, and maintain our Services;</li>
          <li>To process your transactions and manage your account;</li>
          <li>To improve, personalize, and expand our Services;</li>
          <li>To understand and analyze how you use our Services;</li>
          <li>To develop new products, services, features, and functionality;</li>
          <li>To communicate with you, either directly or through one of our partners, including for customer service, to provide you with updates and other information relating to the Service, and for marketing and promotional purposes (with your consent where required);</li>
          <li>To detect and prevent fraud and abuse;</li>
          <li>For compliance purposes, including enforcing our Terms of Service, or other legal rights, or as may be required by applicable laws and regulations or requested by any judicial process or governmental agency.</li>
        </ul>`
    },
    {
      id: "sharing-information",
      icon: <Users className="w-6 h-6 mr-3 text-primary" />,
      title: "4. How We Share Your Information",
      content: `We do not sell your personal information. We may share your information in the following circumstances:
        <ul class="list-disc list-inside space-y-1 mt-2">
          <li><strong>With Service Providers:</strong> We may share your information with third-party vendors, consultants, and other service providers who perform services on our behalf (e.g., payment processing, data hosting, analytics). These service providers are contractually obligated to protect your information and use it only for the purposes for which it was disclosed.</li>
          <li><strong>For Legal Reasons:</strong> We may disclose your information if required to do so by law or in the good faith belief that such action is necessary to comply with a legal obligation, protect and defend our rights or property, prevent or investigate possible wrongdoing in connection with the Services, protect the personal safety of users of the Services or the public, or protect against legal liability.</li>
          <li><strong>Business Transfers:</strong> If WebFiny is involved in a merger, acquisition, or asset sale, your personal information may be transferred. We will provide notice before your personal information is transferred and becomes subject to a different privacy policy.</li>
          <li><strong>With Your Consent:</strong> We may share your information with other third parties with your consent.</li>
        </ul>
        Files you process through our APIs are not shared with third parties, except as necessary to provide the specific API functionality (e.g., using a third-party OCR engine if explicitly part of the service you selected, which would be disclosed).`
    },
    {
      id: "cookies-tracking",
      icon: <Cookie className="w-6 h-6 mr-3 text-primary" />,
      title: "5. Cookies and Tracking Technologies",
      content: `We use cookies and similar tracking technologies (like web beacons and pixels) to track activity on our Services and hold certain information. Cookies are files with a small amount of data which may include an anonymous unique identifier. You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent. However, if you do not accept cookies, you may not be able to use some portions of our Services. For more information, please see our <a href="/cookies" class="text-primary hover:underline">Cookie Policy</a>.`
    },
    {
      id: "data-security",
      icon: <Shield className="w-6 h-6 mr-3 text-primary" />,
      title: "6. Data Security",
      content: `We implement a variety of security measures designed to protect your personal information from unauthorized access, use, alteration, or disclosure. These measures include encryption, access controls, and secure infrastructure. However, no method of transmission over the Internet or method of electronic storage is 100% secure, and we cannot guarantee its absolute security.`
    },
    {
      id: "data-retention",
      icon: <Database className="w-6 h-6 mr-3 text-primary" />,
      title: "7. Data Retention",
      content: `We will retain your personal information only for as long as is necessary for the purposes set out in this Privacy Policy. We will retain and use your information to the extent necessary to comply with our legal obligations (for example, if we are required to retain your data to comply with applicable laws), resolve disputes, and enforce our legal agreements and policies. Processed files are retained only temporarily as needed to provide the service and are then deleted.`
    },
    {
      id: "your-rights",
      icon: <UserCheck className="w-6 h-6 mr-3 text-primary" />,
      title: "8. Your Data Protection Rights",
      content: `Depending on your location and applicable law, you may have certain rights regarding your personal information, including:
        <ul class="list-disc list-inside space-y-1 mt-2">
          <li>The right to access, update, or delete the information we have on you.</li>
          <li>The right of rectification if your information is inaccurate or incomplete.</li>
          <li>The right to object to our processing of your personal information.</li>
          <li>The right to request that we restrict the processing of your personal information.</li>
          <li>The right to data portability (i.e., to receive a copy of your information in a structured, commonly used, and machine-readable format).</li>
          <li>The right to withdraw consent at any time where we relied on your consent to process your personal information.</li>
        </ul>
        You can exercise these rights by contacting us at ${dpoEmail}. We may ask you to verify your identity before responding to such requests.`
    },
    {
      id: "international-transfers",
      icon: <Landmark className="w-6 h-6 mr-3 text-primary" />,
      title: "9. International Data Transfers",
      content: `Your information, including personal data, may be transferred to — and maintained on — computers located outside of your state, province, country, or other governmental jurisdiction where the data protection laws may differ from those from your jurisdiction. If you are located outside the United States and choose to provide information to us, please note that we transfer the data, including personal data, to the United States and process it there. Your consent to this Privacy Policy followed by your submission of such information represents your agreement to that transfer.`
    },
    {
      id: "childrens-privacy",
      icon: <Users className="w-6 h-6 mr-3 text-primary" />,
      title: "10. Children's Privacy",
      content: `Our Services are not intended for use by children under the age of 13 (or 16 in certain jurisdictions). We do not knowingly collect personally identifiable information from children under this age. If you become aware that a child has provided us with personal information, please contact us. If we become aware that we have collected personal information from children without verification of parental consent, we take steps to remove that information from our servers.`
    },
    {
      id: "policy-changes",
      icon: <Info className="w-6 h-6 mr-3 text-primary" />,
      title: "11. Changes to This Privacy Policy",
      content: `We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last Updated" date. You are advised to review this Privacy Policy periodically for any changes. Changes to this Privacy Policy are effective when they are posted on this page.`
    },
    {
      id: "contact-us",
      icon: <Mail className="w-6 h-6 mr-3 text-primary" />,
      title: "12. Contact Us",
      content: `If you have any questions about this Privacy Policy, please contact our Data Protection Officer at: ${companyName}, ${companyAddress}. Email: <a href="mailto:${dpoEmail}" class="text-primary hover:underline">${dpoEmail}</a>.`
    }
  ];

  return (
    <>
      <SchemaOrg
        title={pageTitle}
        description={pageDescription}
        canonicalUrl={canonicalUrl}
        schemas={[
          {
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": pageTitle,
            "description": pageDescription,
            "url": canonicalUrl,
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": canonicalUrl
            },
            "breadcrumb": {
              "@type": "BreadcrumbList",
              "itemListElement": [
                { "@type": "ListItem", "position": 1, "name": "Home", "item": window.location.origin },
                { "@type": "ListItem", "position": 2, "name": "Privacy Policy" }
              ]
            },
            "datePublished": "2023-01-01", 
            "dateModified": "2025-06-15",
            "publisher": {
              "@type": "Organization",
              "name": "WebFiny",
              "logo": {
                "@type": "ImageObject",
                "url": "https://storage.googleapis.com/hostinger-horizons-assets-prod/34687cef-4e75-459d-b6c8-7e9a5f71ffdb/acacfe7044a1c9c74538c545cc0cae87.png"
              }
            }
          }
        ]}
      />
      <div className="bg-gradient-to-b from-background to-muted/30 py-16 md:py-24">
        <div className="container mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12 md:mb-16"
          >
            <Shield className="w-16 h-16 text-primary mx-auto mb-4" />
            <h1 className="text-4xl md:text-5xl font-bold tracking-tight bg-clip-text text-transparent bg-gradient-to-r from-primary via-accent to-secondary mb-4">
              Privacy Policy
            </h1>
            <p className="text-lg text-muted-foreground">Last Updated: {lastUpdated}</p>
            <p className="text-md text-muted-foreground max-w-2xl mx-auto mt-2">
              Your privacy is important to us. This policy explains how we collect, use, and protect your personal information.
            </p>
          </motion.div>

          <div className="max-w-4xl mx-auto bg-card p-6 sm:p-8 md:p-10 rounded-xl shadow-2xl">
            <nav className="mb-10">
              <h2 className="text-xl font-semibold text-foreground mb-3">Table of Contents</h2>
              <ul className="space-y-1 list-decimal list-inside text-primary">
                {sections.map(section => (
                  <li key={section.id}>
                    <a href={`#${section.id}`} className="hover:underline hover:text-accent transition-colors">
                      {section.title.substring(section.title.indexOf(" ") + 1)}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>

            {sections.map((section, index) => (
              <motion.section
                key={section.id}
                id={section.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.05 }}
                className="mb-8 scroll-mt-24"
              >
                <h2 className="flex items-center text-2xl font-semibold text-foreground mb-4 pb-2 border-b border-border">
                  {section.icon} {section.title}
                </h2>
                <div className="prose prose-lg dark:prose-invert max-w-none text-muted-foreground prose-headings:text-foreground prose-a:text-primary hover:prose-a:underline prose-strong:text-foreground">
                  {section.content && <div dangerouslySetInnerHTML={{ __html: section.content }} />}
                  {section.subsections && section.subsections.map((sub, subIndex) => (
                    <div key={subIndex} className="mt-4">
                      <h3 className="text-xl font-semibold text-foreground/90 mb-2">{sub.title}</h3>
                      <div dangerouslySetInnerHTML={{ __html: sub.content }} />
                    </div>
                  ))}
                </div>
              </motion.section>
            ))}
          </div>
        </div>
      </div>
    </>
  );
};

export default PrivacyPolicyPage;